import get from "lodash/get";
import {MutableRefObject, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";

import {useTestRunnerContext} from "@/components/context/TestRunnerContext";
// @ts-expect-error no ts support yet
import TestRunner from "@/legacy/runner/TestRunner.jsx";
// @ts-expect-error no ts support yet
import TransactionScriptEditor from "@/legacy/transaction/TransactionScriptEditor.jsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {MonitoringServiceType, ServiceDefinition} from "@/lib/types/checks";
import {MonitoringGroupsArchMapperEntry, MonitoringServer} from "@/lib/types/monitoring.ts";
import {RunTestResult} from "@/pages/checks/components/form/fields/legacy/RunTestResult.tsx";
import {
  MetaData,
  RunTestTaskStatus,
  TestResult,
} from "@/pages/checks/components/form/fields/legacy/testRunnerTypes.tsx";
import {NetworkRunTest} from "@/pages/checks/components/runtest/network/NetworkRunTest.tsx";
import {RunTestFieldErrors} from "@/pages/checks/components/runtest/network/useNetworkRunTest.ts";

import {filterAllowedMonitoringServersForCheckType} from "../utils";

interface TestRunnerFieldProps {
  publicTestEnabled: boolean;
  hasTxnScript: boolean;
  txnRef?: MutableRefObject<TransactionScriptEditor>;
  runTestLocations: MonitoringServer[];
  service: ServiceDefinition;
  monitoringGroupsArchMapper: MonitoringGroupsArchMapperEntry[];
  runTestURL: string;
  withContextState?: boolean;
}

export const TestRunnerField = ({
  hasTxnScript,
  publicTestEnabled,
  txnRef,
  runTestLocations,
  service,
  monitoringGroupsArchMapper,
  runTestURL,
  withContextState,
}: TestRunnerFieldProps) => {
  const contextState = useTestRunnerContext();

  const [localTestResult, setLocalTestResult] = useState<TestResult | null>(null);
  const [localAffected, setLocalAffected] = useState<string[]>([]);
  const [localSelectedLocation, setLocalSelectedLocation] = useState<MonitoringServer | null>(null);

  const testResult = withContextState && contextState ? contextState.testResult : localTestResult;
  const setTestResult = withContextState && contextState ? contextState.setTestResult : setLocalTestResult;
  const affectedFields = withContextState && contextState ? contextState.affectedFields : localAffected;
  const setAffectedFields = withContextState && contextState ? contextState.setAffectedFields : setLocalAffected;
  const selectedLocation = withContextState && contextState ? contextState.selectedLocation : localSelectedLocation;
  const setSelectedLocation =
    withContextState && contextState ? contextState.setSelectedLocation : setLocalSelectedLocation;

  const {
    watch,
    setError,
    trigger,
    formState: {isValid},
  } = useFormContext();
  let testLocations = publicTestEnabled ? runTestLocations : runTestLocations.filter((l) => l.is_test || l.is_private);

  const monitoringServiceType = watch("monitoring_service_type");
  const mspVersion = watch("msp_version");

  testLocations = filterAllowedMonitoringServersForCheckType({
    monitoringGroupsArchMapper,
    serviceDef: service,
    monitoringServiceType,
    mspVersion,
    monitoringServers: testLocations,
  });

  useEffect(() => {
    if (service.service_type !== monitoringServiceType) {
      setTestResult(null);
      setSelectedLocation(null);
    }
  }, [service.service_type, monitoringServiceType, setTestResult, setSelectedLocation]);

  const handleRunTestStart = () => {
    trigger(affectedFields);
    if (hasTxnScript && txnRef?.current) {
      txnRef.current.runTestStart();
    }
  };

  const handleRunTestError = ({
    fields,
    status,
    serverId,
    result,
    meta,
  }: {
    fields: Record<string, string[]> | undefined;
    serverId?: number;
    result?: string;
    meta?: MetaData | undefined;
    status?: RunTestTaskStatus;
  }) => {
    window.dispatchEvent(new CustomEvent<object>("testRun", {detail: {}}));
    if (fields !== undefined && Object.keys(fields).length > 0) {
      const fieldsArr: string[] = [];
      Object.keys(fields).forEach((key) => {
        setError(key, {message: get(fields, key).join("\n")});
        fieldsArr.push(key);
      });
      if (fieldsArr.length > 0) {
        setAffectedFields(fieldsArr);
      }
    } else {
      const server = runTestLocations.find((l) => l.id === serverId)!;
      setTestResult({
        result: result!,
        status: status || RunTestTaskStatus.FAILED,
        monitoring_server: server,
        meta: meta,
      });
    }
    if (hasTxnScript && txnRef?.current) {
      const validationErrors = get(fields || {}, "msp_script");
      txnRef.current.runTestComplete(null, validationErrors);
    }
  };

  const handleRunTestSuccess = (data: TestResult) => {
    if (hasTxnScript) {
      if (!data.result) {
        handleRunTestError({
          fields: {},
          serverId: data.monitoring_server.id,
          meta: data.meta,
          status: RunTestTaskStatus.FAILED,
          result: "No response received from Probe Server.",
        });
        return;
      }
      txnRef?.current?.runTestComplete(data);
      setTestResult(null);
    } else {
      setTestResult({
        result: data.result,
        status: data.status,
        meta: data.meta,
        monitoring_server: data.monitoring_server,
      });
    }
    window.dispatchEvent(new CustomEvent<object>("testRun", {detail: data}));
  };

  const handleLocationSelect = (location: MonitoringServer) => {
    setSelectedLocation(location);
  };

  if (monitoringServiceType === MonitoringServiceType.NETWORK) {
    // NETWORK uses a dedicated next-gen run-test flow that renders structured
    // probe results + traceroute; the legacy TestRunner is left untouched for
    // every other check type.
    const buildPayload = () => ({
      ...watch(),
      monitoring_service_type: monitoringServiceType,
      test_location: testLocations[0]?.id,
    });
    // Backend validation is the source of truth: field errors it returns (e.g. a
    // missing Locations) are surfaced on the form fields, not inside the panel.
    const onFieldErrors = (fields: RunTestFieldErrors) => {
      const keys = Object.keys(fields);
      keys.forEach((key) => setError(key, {message: get(fields, key).join("\n")}));
      if (keys.length > 0) {
        setAffectedFields(keys);
      }
    };
    return (
      <NetworkRunTest
        runTestURL={runTestURL}
        title="Execution Details"
        buildPayload={buildPayload}
        onFieldErrors={onFieldErrors}
        helpCallout={watch(ServiceField.GLOBAL_PING_NETWORK_CAPTURE_TRACEROUTE) ? "Traceroute enabled" : undefined}
        helpText={
          watch(ServiceField.GLOBAL_PING_NETWORK_CAPTURE_TRACEROUTE)
            ? "this test runs from your selected locations, capturing a network path for each."
            : "This test runs from your selected Global Ping locations."
        }
      />
    );
  }

  return (
    <TestRunner
      locations={testLocations}
      selectedLocation={selectedLocation}
      onLocationSelect={handleLocationSelect}
      onStart={handleRunTestStart}
      onSuccess={handleRunTestSuccess}
      onError={handleRunTestError}
      data={watch()}
      disabled={!isValid}
      scriptGetter={() => {
        const mspScript = watch(ServiceField.SCRIPT);
        return mspScript || "[]";
      }}
      helpText={
        testResult
          ? ""
          : publicTestEnabled
            ? "This test will run from the Uptime.com Probe Server you selected."
            : "This test will run from a dedicated Uptime.com Testing Server or Private Location."
      }
      runTestURL={runTestURL}
      testResult={
        testResult && (
          <RunTestResult
            result={testResult.result}
            testStatus={testResult.status}
            testServer={testResult.monitoring_server}
            meta={testResult.meta}
          />
        )
      }
    />
  );
};
